import { useEffect, useMemo } from 'react';

import { addError } from '@shared/utils/logger';
import { useCarRentalCurrency } from './useCarRentalCurrency';
import { useApiMetadata } from '@shared/hooks/useApiMetadata';
import type { GetCarRatesInput } from '@graphql/generated-base';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import type { CarSearchState, CarVehicle } from '../CarRentalConsole.types';
import { useGetCarRatesLazyQuery } from '../CarRentalConsole.graphql.generated';
import { CarRateProvider, CarRatesSalesChannel, CarRatesSalesEnvironment } from '@graphql/generated-base';
import { combineDateAndTimeToIso, toCarLocationInput, toSupportedCarRatesLanguage } from '../CarRentalConsole.utils';

const SEARCH_RADIUS_KM = 25;
const EMPTY_VEHICLES: ReadonlyArray<CarVehicle> = [];

export const useCarRates = (state: CarSearchState) => {
  const { eventId, deviceType, countryCode } = useApiMetadata();
  const { currency, loading: configLoading, provider, supportedLanguages } = useCarRentalCurrency();

  const language = useMemo(
    () => toSupportedCarRatesLanguage(readNavigatorLanguage(), provider === CarRateProvider.EXPEDIA ? supportedLanguages : undefined),
    [provider, supportedLanguages],
  );

  const derivedInput = useMemo<GetCarRatesInput | null>(() => {
    const pickUpLocationInput = toCarLocationInput(state.pickUpLocation);
    if (!eventId || configLoading || !pickUpLocationInput) return null;
    const dropOffLocationInput = toCarLocationInput(state.dropOffLocation);
    return {
      eventId,
      provider,
      language,
      currency,
      deviceType,
      countryCode,
      driverAge: state.driverAge,
      searchRadiusKm: SEARCH_RADIUS_KM,
      pickUpLocation: pickUpLocationInput,
      salesChannel: CarRatesSalesChannel.WEBSITE,
      salesEnvironment: CarRatesSalesEnvironment.CAR_ONLY,
      dropOffLocation: dropOffLocationInput ?? pickUpLocationInput,
      pickUpTime: combineDateAndTimeToIso(state.pickUpDate, state.pickUpTime),
      dropOffTime: combineDateAndTimeToIso(state.dropOffDate, state.dropOffTime),
    };
  }, [
    eventId,
    provider,
    language,
    currency,
    deviceType,
    countryCode,
    configLoading,
    state.driverAge,
    state.pickUpDate,
    state.pickUpTime,
    state.dropOffDate,
    state.dropOffTime,
    state.pickUpLocation,
    state.dropOffLocation,
  ]);

  const [fetchCarRates, { data, error, previousData, loading: queryLoading, called: queryCalled }] = useGetCarRatesLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    onError: queryError =>
      addError(queryError, {
        eventId,
        context: 'getCarRates',
        errorMessage: queryError.message,
        source: 'Car rates fetch failed (CarRentalConsole)',
      }),
  });

  useEffect(() => {
    if (derivedInput) {
      fetchCarRates({ variables: { input: derivedInput } });
    }
  }, [derivedInput, fetchCarRates]);

  const loading = configLoading || queryLoading || (derivedInput !== null && !queryCalled);
  const effectiveData = data ?? (loading ? undefined : previousData);

  return {
    error,
    loading,
    isInitialLoading: loading && !effectiveData,
    vehicles: effectiveData?.getCarRates?.vehicles ?? EMPTY_VEHICLES,
  };
};
